<template>
  <div id="vm-app" :class="{ scrolled: isScrolled }">
    <!-- Custom Cursor -->
    <div
      class="vm-cursor"
      :style="{ left: cursor.x + 'px', top: cursor.y + 'px' }"
    ></div>
    <div
      class="vm-cursor-ring"
      :style="{ left: cursorRing.x + 'px', top: cursorRing.y + 'px' }"
    ></div>

    <!-- NAV -->
    <nav class="vm-nav" :class="{ scrolled: isScrolled }">
      <a href="#" class="vm-nav-logo">
        <v-img
          max-height="60px"
          max-width="60px"
          src="@/assets/logo.png"
          alt="Dra. Vitória Menezes"
          class="vm-about-img"
        />
      </a>
      <div class="vm-nav-links hidden-sm">
        <a @click.prevent="scrollTo('sobre')" style="color: white">Sobre</a>
        <a @click.prevent="scrollTo('atuacao')" style="color: white">Atuação</a>
        <a @click.prevent="scrollTo('processo')" style="color: white"
          >Processo</a
        >
        <a @click.prevent="openWhatsapp" class="vm-nav-cta">Contato</a>
      </div>
      <button
        class="vm-hamburger hidden-lg"
        @click="mobileOpen = !mobileOpen"
        :class="{ open: mobileOpen }"
      >
        <span></span><span></span><span></span>
      </button>
    </nav>

    <!-- MOBILE MENU -->
    <transition name="menu-slide">
      <div v-if="mobileOpen" class="vm-mobile-menu">
        <a
          @click="
            scrollTo('sobre');
            mobileOpen = false;
          "
          >Sobre</a
        >
        <a
          @click="
            scrollTo('atuacao');
            mobileOpen = false;
          "
          >Atuação</a
        >
        <a
          @click="
            scrollTo('processo');
            mobileOpen = false;
          "
          >Processo</a
        >
        <a @click="openWhatsapp" class="vm-mobile-cta">Agendar Consulta</a>
      </div>
    </transition>

    <!-- HERO -->
    <section id="home" class="vm-hero">
      <div class="vm-hero-bg"></div>
      <div class="vm-hero-grain"></div>
      <div class="vm-hero-vline left"></div>
      <div class="vm-hero-vline right"></div>
      <div class="vm-hero-content">
        <p class="vm-eyebrow">Advocacia Criminal · Defesa Especializada</p>
        <h1 class="vm-hero-title">VITORIA<br /><span>MENEZES</span></h1>
        <div class="vm-hero-sep"></div>
        <p class="vm-hero-sub">
          Defesa técnica com estratégia cirúrgica e compromisso inabalável com
          sua liberdade.<br />
          Cada caso, uma tese. Cada pessoa, um direito.
        </p>
        <div class="vm-hero-actions">
          <a @click.prevent="openWhatsapp" class="vm-btn-primary"
            >Agendar Consulta Privada</a
          >
          <a @click.prevent="scrollTo('sobre')" class="vm-btn-ghost">
            <span class="ghost-line"></span> Conhecer o escritório
          </a>
        </div>
      </div>
      <div class="vm-scroll-hint">
        <div class="vm-scroll-line"></div>
        <p>scroll</p>
      </div>
    </section>

    <!-- STATS -->
    <div class="vm-stats">
      <div v-for="(s, i) in stats" :key="i" class="vm-stat" v-reveal>
        <div class="vm-stat-num">{{ s.num }}</div>
        <div class="vm-stat-lbl">{{ s.lbl }}</div>
      </div>
    </div>

    <!-- ABOUT -->
    <section id="sobre" class="vm-about">
      <div class="vm-about-img-wrap">
        <img
          src="@/assets/eu2.jpeg"
          alt="Dra. Vitória Menezes"
          class="vm-about-img"
        />
        <div class="vm-about-badge">OAB/SC 75.308</div>
      </div>
      <div class="vm-about-content">
        <p class="vm-section-label" v-reveal>Sobre a Advogada</p>
        <h2 class="vm-section-title" v-reveal>
          Rigor técnico.<br /><em>Humanidade</em><br />em cada defesa.
        </h2>
        <p class="vm-body-text" v-reveal>
          Com trajetória dedicada exclusivamente ao Direito Penal, a Dra.
          Vitória Menezes une domínio jurídico apurado e visão estratégica para
          enfrentar os casos mais complexos do sistema criminal brasileiro.
        </p>
        <p class="vm-body-text" v-reveal>
          Graduada e pós-graduada em Ciências Criminais, acredita que a
          liberdade é o bem mais precioso — e que cada cliente merece uma tese
          construída com precisão singular.
        </p>
        <div class="vm-about-divider" v-reveal></div>
        <ul class="vm-cred-list">
          <li v-for="(c, i) in credentials" :key="i" v-reveal>{{ c }}</li>
        </ul>
      </div>
    </section>

    <!-- EXPERTISE -->
    <section id="atuacao" class="vm-expertise">
      <div class="vm-expertise-header">
        <div>
          <p class="vm-section-label" v-reveal>Áreas de Atuação</p>
          <h2 class="vm-section-title dark" v-reveal>
            Defesa onde<br /><em>mais importa.</em>
          </h2>
        </div>
        <p class="vm-expertise-intro" v-reveal>
          Atendimento especializado nas esferas mais sensíveis do Direito Penal,
          com atuação técnica e estratégia construída caso a caso.
        </p>
      </div>
      <div class="vm-expertise-grid">
        <div
          v-for="(area, i) in areas"
          :key="i"
          class="vm-card"
          v-reveal
          @mouseenter="hovered = i"
          @mouseleave="hovered = null"
        >
          <div class="vm-card-num">{{ String(i + 1).padStart(2, "0") }}</div>
          <div class="vm-card-icon" v-html="area.icon"></div>
          <h3 class="vm-card-title">{{ area.title }}</h3>
          <p class="vm-card-desc">{{ area.desc }}</p>
        </div>
      </div>
    </section>

    <!-- PROCESS -->
    <section id="processo" class="vm-process">
      <div class="vm-process-header">
        <p class="vm-section-label" v-reveal>Como Trabalhamos</p>
        <h2 class="vm-section-title" v-reveal>
          Do primeiro contato<br /><em>à sua liberdade.</em>
        </h2>
      </div>
      <div class="vm-steps">
        <div v-for="(step, i) in steps" :key="i" class="vm-step" v-reveal>
          <div class="vm-step-dot">{{ String(i + 1).padStart(2, "0") }}</div>
          <div class="vm-step-body">
            <h4 class="vm-step-title">{{ step.title }}</h4>
            <p class="vm-step-desc">{{ step.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- QUOTE -->
    <div class="vm-quote" v-reveal>
      <div class="vm-quote-mark">"</div>
      <blockquote class="vm-quote-text">
        A liberdade não é um privilégio. É um direito fundamental — e defendê-la
        é a nossa razão de existir.
      </blockquote>
      <p class="vm-quote-attr">— Dra. Vitória Menezes, OAB/SC 75.308</p>
    </div>

    <!-- CONTACT -->
    <section id="contato" class="vm-contact">
      <div class="vm-contact-left">
        <p class="vm-section-label" v-reveal>Fale Conosco</p>
        <h2 class="vm-section-title" v-reveal>
          Seu caso merece<br />atenção <em>agora.</em>
        </h2>
        <p class="vm-contact-tagline" v-reveal>
          Atendimento em todo o território nacional com sigilo absoluto. A
          primeira conversa não tem custo — e pode mudar tudo.
        </p>
        <ul class="vm-contact-details">
          <li v-for="(d, i) in contactDetails" :key="i" v-reveal>
            <span class="vm-detail-label">{{ d.label }}</span>
            <span>{{ d.value }}</span>
          </li>
        </ul>
      </div>
      <div class="vm-contact-form" v-reveal>
        <h3>Análise Gratuita do Caso</h3>
        <p>Respondemos em até 2 horas, com total confidencialidade.</p>
        <div class="vm-form-group">
          <label>Nome completo</label>
          <input v-model="form.name" type="text" placeholder="Seu nome" />
        </div>
        <div class="vm-form-group">
          <label>Telefone / WhatsApp</label>
          <input
            v-model="form.phone"
            type="tel"
            placeholder="(48) 99999-9999"
          />
        </div>
        <div class="vm-form-group">
          <label>Descreva brevemente o caso</label>
          <textarea
            v-model="form.msg"
            placeholder="Contexto da situação..."
          ></textarea>
        </div>
        <button class="vm-form-btn" @click="sendForm">
          Enviar pelo WhatsApp
        </button>
      </div>
    </section>

    <!-- FOOTER -->
    <footer class="vm-footer">
      <div class="vm-footer-top">
        <div class="vm-footer-logo">VITÓRIA MENEZES ADVOCACIA</div>
        <div class="vm-footer-links">
          <a @click.prevent="scrollTo('sobre')" style="color: white">Sobre</a>
          <a @click.prevent="scrollTo('atuacao')" style="color: white"
            >Atuação</a
          >
          <a @click.prevent="scrollTo('contato')" style="color: white"
            >Contato</a
          >
        </div>
      </div>
      <div class="vm-footer-bottom">
        <span
          >© {{ currentYear }} Vitória Menezes Advocacia Criminal — Todos os
          direitos reservados.</span
        >
        <span class="vm-oab">OAB/SC 75.308 · Florianópolis, Brasil</span>
      </div>
    </footer>
  </div>
</template>

<script>
export default {
  name: "VitoriaPage",

  data: () => ({
    isScrolled: false,
    mobileOpen: false,
    hovered: null,
    cursor: { x: 0, y: 0 },
    cursorRing: { x: 0, y: 0 },
    form: { name: "", phone: "", msg: "" },

    stats: [
      { num: "5+", lbl: "Anos de Atuação" },
      { num: "24/7", lbl: "Suporte de Urgência" },
      { num: "100%", lbl: "Sigilo Absoluto" },
    ],

    credentials: [
      "Especialista em execução penal",
      "Membro da Comissão de Direito Penal da OAB/SC",
      "Atuação em todo o território nacional",
    ],

    areas: [
      {
        title: "Execução Penal",
        desc: "Acompanhamento integral na execução da pena: progressão de regime, saída temporária, remição e demais benefícios legais.",
        icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>`,
      },
      {
        title: "Tribunal do Júri",
        desc: "Atuação técnica e estratégica perante o Conselho de Sentença em crimes dolosos contra a vida, com domínio da oratória e psicologia do jurado.",
        icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>`,
      },

      {
        title: "Habeas Corpus",
        desc: "Medidas de urgência em instâncias superiores para garantir o direito de locomoção, com atuação em STJ e STF.",
        icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4"><rect x="3" y="11" width="18" height="11" rx="3"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>`,
      },
      {
        title: "Prisão em Flagrante",
        desc: "Atendimento imediato em casos de prisão em flagrante, preventiva ou temporária, com audiências de custódia e pedidos de liberdade.",
        icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>`,
      },
      {
        title: "Recursos e Revisões",
        desc: "Análise técnica para identificar nulidades, elaboração de recursos nas instâncias ordinárias e superiores e revisões criminais.",
        icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>`,
      },
      {
        title: "Ação Penal",
        desc: "Atuação estratégica em todas as fases da ação penal, desde o recebimento da denúncia até a sentença, com construção de teses defensivas sólidas e foco na absolvição ou na mitigação de penas.",
        icon: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4"><rect x="2" y="7" width="20" height="14" rx="3"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/><line x1="12" y1="12" x2="12" y2="16"/><line x1="10" y1="14" x2="14" y2="14"/></svg>`,
      },
    ],

    steps: [
      {
        title: "Consulta Sigilosa",
        desc: "Atendimento reservado para análise completa da situação, com escuta ativa e orientação imediata, sem julgamentos.",
      },
      {
        title: "Diagnóstico do Caso",
        desc: "Estudo detalhado dos autos, identificação de nulidades, produção de provas e definição da melhor estratégia defensiva.",
      },
      {
        title: "Tese Personalizada",
        desc: "Construção de uma defesa sob medida, com argumentação técnica robusta, fundamentada na jurisprudência mais atual.",
      },
      {
        title: "Acompanhamento Total",
        desc: "Presença em cada etapa processual, com comunicação transparente e relatórios periódicos ao cliente e à família.",
      },
    ],

    contactDetails: [
      { label: "WhatsApp", value: "+55 48 99634-6811" },
      { label: "E-mail", value: "vitorialausmenezes.adv@gmail.com" },
      { label: "Cidade", value: "Florianópolis, Santa Catarina" },
      { label: "Urgência", value: "Disponível 24 horas por dia" },
    ],
  }),

  computed: {
    currentYear() {
      return new Date().getFullYear();
    },
  },

  directives: {
    reveal: {
      mounted(el) {
        el.classList.add("vm-reveal");
        const obs = new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting) {
              el.classList.add("vm-visible");
              obs.disconnect();
            }
          },
          { threshold: 0.12 },
        );
        obs.observe(el);
      },
    },
  },

  methods: {
    scrollTo(id) {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      this.mobileOpen = false;
    },
    openWhatsapp() {
      window.open("https://wa.me/5548996346811", "_blank");
    },
    sendForm() {
      const text = encodeURIComponent(
        `Olá, Dra. Vitória!\n\nNome: ${this.form.name}\nTelefone: ${this.form.phone}\n\n${this.form.msg}`,
      );
      window.open(`https://wa.me/5548996346811?text=${text}`, "_blank");
    },
    initCursor() {
      let mx = 0,
        my = 0;
      document.addEventListener("mousemove", (e) => {
        mx = e.clientX;
        my = e.clientY;
      });
      const tick = () => {
        this.cursor = { x: mx, y: my };
        this.cursorRing.x += (mx - this.cursorRing.x) * 0.12;
        this.cursorRing.y += (my - this.cursorRing.y) * 0.12;
        requestAnimationFrame(tick);
      };
      tick();
    },
  },

  mounted() {
    this.initCursor();
    window.addEventListener("scroll", () => {
      this.isScrolled = window.scrollY > 60;
    });
  },
};
</script>

<style>
@import url("https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=DM+Sans:wght@200;300;400;500&display=swap");

/* ───── TOKENS ───── */
:root {
  --ink: #181715;
  --sand: #c8b89a;
  --sand-lt: #e8ddd0;
  --sand-bg: #d4c4b0;
  --cream: #f5f0e8;
  --stone: #7a6f64;
  --warm-white: #faf8f4;
  --r-sm: 12px;
  --r-md: 20px;
  --r-lg: 32px;
  --r-xl: 48px;
}

*,
*::before,
*::after {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}
html {
  scroll-behavior: smooth;
}

#vm-app {
  font-family: "DM Sans", sans-serif;
  background: var(--ink);
  color: var(--cream);
  overflow-x: hidden;
  cursor: none;
}

/* ───── CURSOR ───── */
.vm-cursor {
  position: fixed;
  width: 8px;
  height: 8px;
  background: var(--sand);
  border-radius: 50%;
  pointer-events: none;
  z-index: 9999;
  transform: translate(-50%, -50%);
  transition: width 0.25s, height 0.25s;
}
.vm-cursor-ring {
  position: fixed;
  width: 32px;
  height: 32px;
  border: 1px solid rgba(200, 184, 154, 0.45);
  border-radius: 50%;
  pointer-events: none;
  z-index: 9998;
  transform: translate(-50%, -50%);
}

/* ───── NAV ───── */
.vm-nav {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  padding: 28px 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: background 0.5s, padding 0.4s, box-shadow 0.5s;
}
.vm-nav.scrolled {
  background: rgba(24, 23, 21, 0.93);
  backdrop-filter: blur(16px);
  padding: 16px 60px;
  box-shadow: 0 1px 0 rgba(200, 184, 154, 0.12);
}
.vm-nav-logo {
  font-family: "Cormorant Garamond", serif;
  font-size: 1.25rem;
  font-weight: 300;
  letter-spacing: 6px;
  color: var(--sand);
  text-decoration: none;
}
.vm-nav-links {
  display: flex;
  align-items: center;
  gap: 44px;
}
.vm-nav-links a {
  color: rgba(245, 240, 232, 0.55);
  font-size: 0.76rem;
  font-weight: 300;
  letter-spacing: 2.5px;
  text-transform: uppercase;
  text-decoration: none;
  cursor: none;
  transition: color 0.3s;
}
.vm-nav-links a:hover {
  color: var(--sand);
}
.vm-nav-cta {
  border: 1px solid var(--sand) !important;
  color: var(--sand) !important;
  padding: 9px 26px !important;
  border-radius: 100px !important;
  transition: background 0.3s, color 0.3s !important;
}
.vm-nav-cta:hover {
  background: var(--sand) !important;
  color: var(--ink) !important;
}

/* hamburger */
.vm-hamburger {
  display: none;
  flex-direction: column;
  gap: 6px;
  background: none;
  border: none;
  cursor: none;
  padding: 4px;
}
.vm-hamburger span {
  display: block;
  width: 24px;
  height: 1px;
  background: var(--cream);
  transition: 0.3s;
}
.vm-hamburger.open span:nth-child(1) {
  transform: translateY(7px) rotate(45deg);
}
.vm-hamburger.open span:nth-child(2) {
  opacity: 0;
}
.vm-hamburger.open span:nth-child(3) {
  transform: translateY(-7px) rotate(-45deg);
}

/* ───── MOBILE MENU ───── */
.vm-mobile-menu {
  position: fixed;
  inset: 0;
  z-index: 90;
  background: var(--ink);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 40px;
}
.vm-mobile-menu a {
  font-family: "Cormorant Garamond", serif;
  font-size: 2.8rem;
  font-weight: 300;
  color: var(--cream);
  text-decoration: none;
  letter-spacing: 4px;
  transition: color 0.3s;
  cursor: none;
}
.vm-mobile-menu a:hover {
  color: var(--sand);
}
.vm-mobile-cta {
  font-size: 1rem !important;
  border: 1px solid var(--sand);
  padding: 14px 40px;
  border-radius: 100px;
  color: var(--sand) !important;
  margin-top: 16px;
}
.menu-slide-enter-active,
.menu-slide-leave-active {
  transition: opacity 0.4s;
}
.menu-slide-enter-from,
.menu-slide-leave-to {
  opacity: 0;
}

/* ───── HERO ───── */
.vm-hero {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  overflow: hidden;
}
.vm-hero-bg {
  position: absolute;
  inset: 0;

  background-image: linear-gradient(
      to right,
      var(--ink) 37%,
      rgba(24, 23, 21, 0.6) 55%,
      transparent 90%
    ),
    url("@/assets/eu.jpeg");

  background-size: 100% 100%, 60% auto;
  background-repeat: no-repeat;
  background-position: left, right center;

  filter: brightness(0.35) saturate(0.7);

  transform: scale(1.06);
  animation: hz-slowzoom 22s ease-out forwards;
}

@media (max-width: 768px) {
  .vm-hero-bg {
    background-image: url("@/assets/eu.jpeg");

    background-size: cover;
    background-position: center top;

    filter: brightness(0.15) saturate(0.7);
  }
}

@keyframes hz-slowzoom {
  to {
    transform: scale(1);
  }
}
.vm-hero-grain {
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: 0.35;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='.06'/%3E%3C/svg%3E");
}
.vm-hero-vline {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 1px;
  background: linear-gradient(
    to bottom,
    transparent,
    rgba(200, 184, 154, 0.2) 25%,
    rgba(200, 184, 154, 0.2) 75%,
    transparent
  );
}
.vm-hero-vline.left {
  left: 56px;
}
.vm-hero-vline.right {
  right: 56px;
}

.vm-hero-content {
  position: relative;
  z-index: 2;
  padding: 0 120px;
  max-width: 1040px;
}
.vm-eyebrow {
  font-size: 0.68rem;
  font-weight: 300;
  letter-spacing: 6px;
  text-transform: uppercase;
  color: var(--sand);
  margin-bottom: 32px;
  opacity: 0;
  animation: hz-fadeup 0.8s 0.4s forwards;
}
.vm-hero-title {
  font-family: "Cormorant Garamond", serif;
  font-size: clamp(4.5rem, 11vw, 10rem);
  font-weight: 600;
  line-height: 0.9;
  color: var(--cream);
  letter-spacing: -1px;
  margin-bottom: 40px;
  opacity: 0;
  animation: hz-fadeup 0.9s 0.6s forwards;
}
.vm-hero-title span {
  color: var(--sand-lt);
  font-weight: 300;
}
.vm-hero-sep {
  width: 72px;
  height: 1.5px;
  background: var(--sand);
  margin-bottom: 28px;
  opacity: 0;
  transform-origin: left;
  animation: hz-line 0.8s 0.85s forwards;
}
@keyframes hz-line {
  from {
    opacity: 0;
    transform: scaleX(0);
  }
  to {
    opacity: 1;
    transform: scaleX(1);
  }
}
.vm-hero-sub {
  font-size: 1.05rem;
  font-weight: 200;
  line-height: 1.9;
  color: rgba(245, 240, 232, 0.55);
  max-width: 500px;
  margin-bottom: 52px;
  opacity: 0;
  animation: hz-fadeup 0.8s 1.05s forwards;
}
.vm-hero-actions {
  display: flex;
  align-items: center;
  gap: 40px;
  opacity: 0;
  animation: hz-fadeup 0.8s 1.25s forwards;
}
.vm-btn-primary {
  background: var(--sand);
  color: var(--ink);
  text-decoration: none;
  cursor: none;
  font-size: 0.72rem;
  font-weight: 500;
  letter-spacing: 3px;
  text-transform: uppercase;
  padding: 17px 44px;
  border-radius: 100px;
  transition: background 0.3s, transform 0.3s;
  color: black !important;
}
.vm-btn-primary:hover {
  background: var(--sand-lt);
  transform: translateY(-2px);
}
.vm-btn-ghost {
  display: flex;
  align-items: center;
  gap: 14px;
  font-size: 0.72rem;
  font-weight: 200;
  letter-spacing: 2.5px;
  text-transform: uppercase;
  color: rgba(245, 240, 232, 0.45);
  text-decoration: none;
  cursor: none;
  transition: color 0.3s;
  color: white !important;
}
.vm-btn-ghost:hover {
  color: var(--sand);
}
.ghost-line {
  display: block;
  width: 30px;
  height: 1px;
  background: currentColor;
  color: white;
}

.vm-scroll-hint {
  position: absolute;
  bottom: 36px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  opacity: 0;
  animation: hz-fadeup 0.7s 1.8s forwards;
}
.vm-scroll-hint p {
  font-size: 0.6rem;
  letter-spacing: 4px;
  text-transform: uppercase;
  color: rgba(245, 240, 232, 0.25);
}
.vm-scroll-line {
  width: 1px;
  height: 48px;
  background: linear-gradient(to bottom, var(--sand), transparent);
  animation: hz-pulse 2s 2s infinite;
}
@keyframes hz-pulse {
  0%,
  100% {
    opacity: 0.3;
  }
  50% {
    opacity: 1;
  }
}
@keyframes hz-fadeup {
  from {
    opacity: 0;
    transform: translateY(22px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ───── STATS ───── */
.vm-stats {
  background: var(--sand-bg);
  display: grid;
  grid-template-columns: repeat(3, 1fr);
}
.vm-stat {
  padding: 44px 60px;
  border-right: 1px solid rgba(24, 23, 21, 0.15);
  transition: background 0.3s;
}
.vm-stat:last-child {
  border-right: none;
}
.vm-stat:hover {
  background: var(--sand-lt);
}
.vm-stat-num {
  font-family: "Cormorant Garamond", serif;
  font-size: 3.2rem;
  font-weight: 300;
  color: var(--ink);
  line-height: 1;
  margin-bottom: 6px;
}
.vm-stat-lbl {
  font-size: 0.68rem;
  font-weight: 400;
  letter-spacing: 2.5px;
  text-transform: uppercase;
  color: rgba(24, 23, 21, 0.55);
}

/* ───── ABOUT ───── */
.vm-about {
  background: var(--warm-white);
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: 90vh;
}
.vm-about-img-wrap {
  position: relative;
  overflow: hidden;
}
.vm-about-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top;
  filter: grayscale(15%) contrast(1.04);
  transition: transform 8s ease;
}
.vm-about-img-wrap:hover .vm-about-img {
  transform: scale(1.04);
}
.vm-about-badge {
  position: absolute;
  bottom: 36px;
  left: 36px;
  background: var(--ink);
  color: var(--sand);
  font-size: 0.65rem;
  font-weight: 400;
  letter-spacing: 3px;
  text-transform: uppercase;
  padding: 12px 24px;
  border-radius: 100px;
}
.vm-about-content {
  background: var(--ink);
  padding: 100px 80px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.vm-section-label {
  font-size: 0.63rem;
  font-weight: 300;
  letter-spacing: 6px;
  text-transform: uppercase;
  color: var(--sand);
  margin-bottom: 20px;
}
.vm-section-title {
  font-family: "Cormorant Garamond", serif;
  font-size: clamp(2rem, 4vw, 3.2rem);
  font-weight: 300;
  line-height: 1.1;
  color: var(--cream);
  margin-bottom: 36px;
}
.vm-section-title em {
  font-style: italic;
  color: var(--sand);
}
.vm-section-title.dark {
  color: var(--ink);
}
.vm-section-title.dark em {
  color: rgba(24, 23, 21, 0.5);
}
.vm-body-text {
  font-size: 0.93rem;
  font-weight: 200;
  line-height: 1.95;
  color: rgba(245, 240, 232, 0.5);
  margin-bottom: 20px;
}
.vm-about-divider {
  width: 48px;
  height: 1.5px;
  background: var(--sand);
  margin: 28px 0;
}
.vm-cred-list {
  list-style: none;
}
.vm-cred-list li {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  font-size: 0.84rem;
  font-weight: 200;
  color: rgba(245, 240, 232, 0.45);
  padding: 12px 0;
  border-bottom: 1px solid rgba(245, 240, 232, 0.06);
}
.vm-cred-list li::before {
  content: "—";
  color: var(--sand);
  flex-shrink: 0;
}

/* ───── EXPERTISE ───── */
.vm-expertise {
  background: var(--cream);
  padding: 120px 60px;
}
.vm-expertise-header {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: end;
  margin-bottom: 64px;
}
.vm-expertise-intro {
  font-size: 0.93rem;
  font-weight: 300;
  line-height: 1.9;
  color: rgba(24, 23, 21, 0.5);
  padding-top: 24px;
  border-top: 1px solid rgba(24, 23, 21, 0.1);
}
.vm-expertise-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}
.vm-card {
  background: var(--ink);
  padding: 52px 44px;
  border-radius: var(--r-lg);
  position: relative;
  overflow: hidden;
  transition: transform 0.4s, box-shadow 0.4s;
  cursor: none;
}
.vm-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: var(--sand);
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: var(--r-lg) var(--r-lg) 0 0;
}
.vm-card:hover::before {
  transform: scaleX(1);
}
.vm-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.25);
}
.vm-card-num {
  position: absolute;
  top: 24px;
  right: 32px;
  font-family: "Cormorant Garamond", serif;
  font-size: 4.5rem;
  font-weight: 300;
  color: rgba(200, 184, 154, 0.07);
  line-height: 1;
}
.vm-card-icon {
  width: 48px;
  height: 48px;
  color: var(--sand);
  margin-bottom: 28px;
}
.vm-card-icon svg {
  width: 100%;
  height: 100%;
}
.vm-card-title {
  font-family: "Cormorant Garamond", serif;
  font-size: 1.5rem;
  font-weight: 400;
  color: var(--cream);
  margin-bottom: 14px;
}
.vm-card-desc {
  font-size: 0.82rem;
  font-weight: 200;
  line-height: 1.85;
  color: rgba(245, 240, 232, 0.38);
}

/* ───── PROCESS ───── */
.vm-process {
  background: var(--ink);
  padding: 120px 60px;
}
.vm-process-header {
  text-align: center;
  margin-bottom: 72px;
}
.vm-steps {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
}
.vm-step {
  background: #1f1d1b;
  border-radius: var(--r-lg);
  padding: 48px 36px;
  transition: transform 0.4s;
}
.vm-step:hover {
  transform: translateY(-4px);
}
.vm-step-dot {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  border: 1px solid rgba(200, 184, 154, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Cormorant Garamond", serif;
  font-size: 1.3rem;
  font-weight: 300;
  color: var(--sand);
  margin-bottom: 28px;
  transition: background 0.4s, border-color 0.4s;
}
.vm-step:hover .vm-step-dot {
  background: var(--sand);
  color: var(--ink);
  border-color: var(--sand);
}
.vm-step-title {
  font-family: "Cormorant Garamond", serif;
  font-size: 1.2rem;
  font-weight: 400;
  color: var(--cream);
  margin-bottom: 12px;
}
.vm-step-desc {
  font-size: 0.8rem;
  font-weight: 200;
  line-height: 1.85;
  color: rgba(245, 240, 232, 0.38);
}

/* ───── QUOTE ───── */
.vm-quote {
  background: var(--sand-bg);
  padding: 100px 120px;
  text-align: center;
}
.vm-quote-mark {
  font-family: "Cormorant Garamond", serif;
  font-size: 7rem;
  font-weight: 300;
  line-height: 0.4;
  color: rgba(24, 23, 21, 0.15);
  margin-bottom: -8px;
}
.vm-quote-text {
  font-family: "Cormorant Garamond", serif;
  font-size: clamp(1.7rem, 3.5vw, 2.6rem);
  font-weight: 300;
  font-style: italic;
  color: var(--ink);
  line-height: 1.45;
  max-width: 820px;
  margin: 0 auto 28px;
}
.vm-quote-attr {
  font-size: 0.67rem;
  font-weight: 400;
  letter-spacing: 3.5px;
  text-transform: uppercase;
  color: rgba(24, 23, 21, 0.45);
}

/* ───── CONTACT ───── */
.vm-contact {
  background: var(--ink);
  padding: 120px 60px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 100px;
  align-items: start;
}
.vm-contact-tagline {
  font-size: 0.93rem;
  font-weight: 200;
  line-height: 1.9;
  color: rgba(245, 240, 232, 0.4);
  margin-bottom: 52px;
}
.vm-contact-details {
  list-style: none;
}
.vm-contact-details li {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 18px 0;
  border-bottom: 1px solid rgba(245, 240, 232, 0.06);
  font-size: 0.85rem;
  font-weight: 200;
  color: rgba(245, 240, 232, 0.5);
}
.vm-detail-label {
  font-size: 0.58rem;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: var(--sand);
  min-width: 76px;
}
.vm-contact-form {
  background: #1f1d1b;
  padding: 60px 56px;
  border-radius: var(--r-xl);
  border: 1px solid rgba(200, 184, 154, 0.1);
}
.vm-contact-form h3 {
  font-family: "Cormorant Garamond", serif;
  font-size: 1.8rem;
  font-weight: 300;
  color: var(--cream);
  margin-bottom: 6px;
}
.vm-contact-form p {
  font-size: 0.8rem;
  font-weight: 200;
  color: rgba(245, 240, 232, 0.3);
  margin-bottom: 36px;
}
.vm-form-group {
  margin-bottom: 24px;
}
.vm-form-group label {
  display: block;
  font-size: 0.58rem;
  font-weight: 400;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: var(--sand);
  margin-bottom: 10px;
}
.vm-form-group input,
.vm-form-group textarea {
  width: 100%;
  background: rgba(245, 240, 232, 0.04);
  border: 1px solid rgba(245, 240, 232, 0.08);
  border-radius: var(--r-sm);
  color: var(--cream);
  font-family: "DM Sans", sans-serif;
  font-size: 0.9rem;
  font-weight: 200;
  padding: 14px 18px;
  outline: none;
  transition: border-color 0.3s;
}
.vm-form-group input:focus,
.vm-form-group textarea:focus {
  border-color: rgba(200, 184, 154, 0.45);
}
.vm-form-group textarea {
  resize: none;
  height: 110px;
}
.vm-form-btn {
  width: 100%;
  background: var(--sand);
  color: var(--ink);
  border: none;
  border-radius: 100px;
  font-family: "DM Sans", sans-serif;
  font-size: 0.72rem;
  font-weight: 500;
  letter-spacing: 3.5px;
  text-transform: uppercase;
  padding: 18px;
  cursor: none;
  transition: background 0.3s, transform 0.3s;
}
.vm-form-btn:hover {
  background: var(--sand-lt);
  transform: translateY(-2px);
}

/* ───── FOOTER ───── */
.vm-footer {
  background: #111010;
  padding: 52px 60px 36px;
  border-top: 1px solid rgba(200, 184, 154, 0.1);
}
.vm-footer-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 32px;
  border-bottom: 1px solid rgba(245, 240, 232, 0.06);
  margin-bottom: 24px;
}
.vm-footer-logo {
  font-family: "Cormorant Garamond", serif;
  font-size: 1rem;
  font-weight: 300;
  letter-spacing: 5px;
  color: var(--sand);
}
.vm-footer-links {
  display: flex;
  gap: 36px;
}
.vm-footer-links a {
  font-size: 0.67rem;
  font-weight: 300;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: rgba(245, 240, 232, 0.25);
  text-decoration: none;
  cursor: none;
  transition: color 0.3s;
}
.vm-footer-links a:hover {
  color: var(--sand);
}
.vm-footer-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.7rem;
  font-weight: 200;
  color: rgba(245, 240, 232, 0.18);
  letter-spacing: 0.5px;
}
.vm-oab {
  color: rgba(200, 184, 154, 0.35);
}

/* ───── REVEAL ───── */
.vm-reveal {
  opacity: 0;
  transform: translateY(36px);
  transition: opacity 0.9s, transform 0.9s cubic-bezier(0.4, 0, 0.2, 1);
}
.vm-visible {
  opacity: 1;
  transform: translateY(0);
}

/* ───── RESPONSIVE ───── */
.hidden-sm {
  display: flex;
}
.hidden-lg {
  display: none;
}

@media (max-width: 960px) {
  .hidden-sm {
    display: none;
  }
  .hidden-lg {
    display: flex;
  }
  .vm-nav {
    padding: 22px 28px;
  }
  .vm-nav.scrolled {
    padding: 14px 28px;
  }
  .vm-hero-content {
    padding: 0 36px;
  }
  .vm-hero-vline {
    display: none;
  }
  .vm-stats {
    grid-template-columns: 1fr;
  }
  .vm-stat {
    border-right: none;
    border-bottom: 1px solid rgba(24, 23, 21, 0.1);
    padding: 32px 36px;
  }
  .vm-about {
    grid-template-columns: 1fr;
  }
  .vm-about-img-wrap {
    height: 380px;
  }
  .vm-about-content {
    padding: 64px 36px;
  }
  .vm-expertise {
    padding: 80px 24px;
  }
  .vm-expertise-header {
    grid-template-columns: 1fr;
    gap: 28px;
  }
  .vm-expertise-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  .vm-process {
    padding: 80px 24px;
  }
  .vm-steps {
    grid-template-columns: 1fr 1fr;
    gap: 14px;
  }
  .vm-quote {
    padding: 80px 36px;
  }
  .vm-contact {
    grid-template-columns: 1fr;
    padding: 80px 24px;
    gap: 56px;
  }
  .vm-contact-form {
    padding: 40px 28px;
  }
  .vm-footer {
    padding: 40px 24px 28px;
  }
  .vm-footer-top {
    flex-direction: column;
    gap: 24px;
    text-align: center;
  }
  .vm-footer-bottom {
    flex-direction: column;
    gap: 10px;
    text-align: center;
  }
}

@media (max-width: 580px) {
  .vm-steps {
    grid-template-columns: 1fr;
  }
  .vm-hero-title {
    font-size: 3.8rem;
  }
}
</style>
